import React, { useState, useEffect, useRef } from "react";
import {
  trackQuestionAnswer,
  trackQuestionsComplete,
  trackGoBack,
  trackQuestionViewed,
  trackQuestionAbandonment
} from '../utils/analytics';
import ContactForm from './ContactForm';
import TextInput from './TextInput';
import MultipleChoice from './MultipleChoice';
import YesNoQuestion from './YesNoQuestion';

function QuestionFlow({ onComplete, onBack, answers, setAnswers, questionHistory, setQuestionHistory }) {
  const [data, setData] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track whether we're navigating normally (vs true abandonment)
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    // Fetch the questions from /questions.json
    fetch("/questions.json")
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to fetch questions: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then(json => {
        // Filter out disabled categories
        const enabledData = {
          ...json,
          categories: json.categories.filter(cat => cat.enabled !== false)
        };
        setData(enabledData);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching questions.json:", err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Calculate total questions across all categories
  const getTotalQuestions = () => {
    if (!data || !data.categories) return 0;
    return data.categories.reduce((total, category) => total + category.questions.length, 0);
  };

  // Get the current question number (1-based) across all categories
  const getCurrentQuestionNumber = () => {
    if (!data) return 0;
    let questionNumber = 0;
    for (let i = 0; i < currentCategoryIndex; i++) {
      questionNumber += data.categories[i].questions.length;
    }
    return questionNumber + currentQuestionIndex + 1;
  };

  // Track when the current question is viewed
  useEffect(() => {
    if (data && data.categories && data.categories[currentCategoryIndex]) {
      const currentQuestion = data.categories[currentCategoryIndex].questions[currentQuestionIndex];
      if (currentQuestion) {
        // Reset navigation flag when viewing a new question
        isNavigatingRef.current = false;

        trackQuestionViewed(
          currentQuestion.id,
          currentCategoryIndex,
          currentQuestionIndex,
          getTotalQuestions()
        );
      }
    }
  }, [currentCategoryIndex, currentQuestionIndex, data, getTotalQuestions]);

  // Track abandonment only on true component unmount (not question-to-question navigation)
  useEffect(() => {
    return () => {
      // Only track if this is true abandonment (not normal navigation)
      if (!isNavigatingRef.current && data && data.categories && data.categories[currentCategoryIndex]) {
        const totalQuestions = getTotalQuestions();
        const currentQuestionNumber = getCurrentQuestionNumber();

        // If we're not at the last question, this is abandonment
        if (currentQuestionNumber < totalQuestions) {
          const currentQuestion = data.categories[currentCategoryIndex].questions[currentQuestionIndex];
          if (currentQuestion) {
            trackQuestionAbandonment(
              currentQuestion.id,
              currentCategoryIndex,
              currentQuestionIndex,
              totalQuestions
            );
          }
        }
      }
    };
  }, [currentCategoryIndex, currentQuestionIndex, data, getTotalQuestions, getCurrentQuestionNumber]);

  // Auto-advance logic for questions that should be skipped
  useEffect(() => {
    if (data && data.categories && data.categories[currentCategoryIndex]) {
      const currentCategory = data.categories[currentCategoryIndex];
      const currentQuestion = currentCategory.questions[currentQuestionIndex];
      if (currentQuestion && !shouldShowQuestion(currentQuestion)) {
        goToNextQuestion();
      }
    }
  }, [currentCategoryIndex, currentQuestionIndex, data]);

  // Store answer without navigating (for manual next button)
  const handleAnswerChange = (answer) => {
    if (!data) return;

    // Store the user's answer
    const updatedAnswers = {
      ...answers,
      [currentQuestion.id]: answer
    };
    setAnswers(updatedAnswers);
  };

  // Store answer AND navigate (for yes/no auto-advance)
  const handleAnswerSubmit = (answer) => {
    if (!data) return;

    const totalQuestions = getTotalQuestions();

    // Mark that we're navigating normally (not abandoning)
    isNavigatingRef.current = true;

    // Track the question answer
    trackQuestionAnswer(
      currentQuestion.id,
      typeof answer === 'object' ? JSON.stringify(answer) : answer,
      currentCategoryIndex,
      currentQuestionIndex,
      totalQuestions
    );

    // Store the current position in history for back button
    setQuestionHistory([
      ...questionHistory,
      { categoryIndex: currentCategoryIndex, questionIndex: currentQuestionIndex }
    ]);

    // Store the user's answer
    const updatedAnswers = {
      ...answers,
      [currentQuestion.id]: answer
    };
    setAnswers(updatedAnswers);

    // Move to next question or next category
    const nextQuestionIndex = currentQuestionIndex + 1;
    if (nextQuestionIndex < currentCategory.questions.length) {
      setCurrentQuestionIndex(nextQuestionIndex);
    } else {
      // Move to next category
      const nextCategoryIndex = currentCategoryIndex + 1;
      if (nextCategoryIndex < data.categories.length) {
        setCurrentCategoryIndex(nextCategoryIndex);
        setCurrentQuestionIndex(0);
      } else {
        // Done with all questions - track completion
        const totalQuestions = getTotalQuestions();
        trackQuestionsComplete(Object.keys(updatedAnswers).length, totalQuestions);
        onComplete(updatedAnswers, totalQuestions);
      }
    }
  };

  const shouldShowQuestion = (question) => {
    if (!question.depends_on) return true;
    
    const dependentAnswer = answers[question.depends_on];
    return dependentAnswer === question.depends_value;
  };

  const renderQuestionInput = () => {
    const question = currentQuestion;
    const currentAnswer = answers[question.id];

    switch (question.type) {
      case 'contact_list':
        return (
          <ContactForm
            question={question}
            value={currentAnswer}
            onChange={handleAnswerChange}
          />
        );

      case 'text':
        return (
          <TextInput
            question={question}
            value={currentAnswer}
            onChange={handleAnswerChange}
          />
        );

      case 'multiple_choice':
        return (
          <MultipleChoice
            question={question}
            value={currentAnswer}
            onChange={handleAnswerChange}
          />
        );

      case 'yes_no':
        return (
          <YesNoQuestion
            question={question}
            value={currentAnswer}
            onChange={handleAnswerSubmit}
          />
        );

      default:
        return <div>Unknown question type: {question.type}</div>;
    }
  };

  const canProceed = () => {
    const currentAnswer = answers[currentQuestion.id];
    
    switch (currentQuestion.type) {
      case 'contact_list':
        return Array.isArray(currentAnswer) && currentAnswer.length > 0;
      case 'text':
        return currentAnswer && currentAnswer.trim().length > 0;
      case 'multiple_choice':
        return currentAnswer && currentAnswer.main;
      case 'yes_no':
        return currentAnswer && (currentAnswer === 'Yes' || currentAnswer === 'No');
      default:
        return false;
    }
  };


  const goToNextQuestion = () => {
    if (!data) return;

    const totalQuestions = getTotalQuestions();
    const currentAnswer = answers[currentQuestion.id];

    // Mark that we're navigating normally (not abandoning)
    isNavigatingRef.current = true;

    // Track the question answer
    trackQuestionAnswer(
      currentQuestion.id,
      typeof currentAnswer === 'object' ? JSON.stringify(currentAnswer) : currentAnswer,
      currentCategoryIndex,
      currentQuestionIndex,
      totalQuestions
    );

    // Store the current position in history for back button
    setQuestionHistory([
      ...questionHistory,
      { categoryIndex: currentCategoryIndex, questionIndex: currentQuestionIndex }
    ]);

    const nextQuestionIndex = currentQuestionIndex + 1;
    if (nextQuestionIndex < currentCategory.questions.length) {
      setCurrentQuestionIndex(nextQuestionIndex);
    } else {
      // Move to next category
      const nextCategoryIndex = currentCategoryIndex + 1;
      if (nextCategoryIndex < data.categories.length) {
        setCurrentCategoryIndex(nextCategoryIndex);
        setCurrentQuestionIndex(0);
      } else {
        // Done with all questions
        const totalQuestions = getTotalQuestions();
        trackQuestionsComplete(Object.keys(answers).length, totalQuestions);
        onComplete(answers, totalQuestions);
      }
    }
  };

  const handleGoBack = () => {
    const currentQuestionNumber = getCurrentQuestionNumber();

    // Mark that we're navigating normally (not abandoning)
    isNavigatingRef.current = true;

    trackGoBack('questions', currentQuestionNumber);

    if (questionHistory.length > 0) {
      // Get the previous position
      const prevPosition = questionHistory[questionHistory.length - 1];

      // Set the indices to the previous position
      setCurrentCategoryIndex(prevPosition.categoryIndex);
      setCurrentQuestionIndex(prevPosition.questionIndex);

      // Remove the used history entry
      setQuestionHistory(questionHistory.slice(0, questionHistory.length - 1));

      // Remove the answer for the current question
      if (currentQuestion && currentQuestion.id) {
        const newAnswers = { ...answers };
        delete newAnswers[currentQuestion.id];
        setAnswers(newAnswers);
      }
    } else {
      // If no history, go back to welcome page
      onBack();
    }
  };

  if (loading) return <div>Loading questions...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!data || !data.categories || data.categories.length === 0) return <div>No questions available</div>;

  // Access current question
  const currentCategory = data.categories[currentCategoryIndex];
  const currentQuestion = currentCategory.questions[currentQuestionIndex];

  // Skip questions that don't meet dependency requirements
  if (!shouldShowQuestion(currentQuestion)) {
    return <div>Loading next question...</div>;
  }

  return (
    <div className="question-flow">
      <div className="question-header">
        <div className="category-header-container">
          <button 
            className="back-button" 
            onClick={handleGoBack}
            aria-label="Go back"
          >
            <span>←</span>
          </button>
          <h2 className="category-title">{currentCategory.name}</h2>
        </div>
      </div>

      <div className="question-card">
        <div className="question-content">
          {renderQuestionInput()}
        </div>

        {canProceed() && currentQuestion.type !== 'yes_no' && (
          <div className="navigation-buttons">
            <button
              className="next-button"
              onClick={goToNextQuestion}
            >
              Next Question →
            </button>
          </div>
        )}
      </div>
      
      <div className="question-progress">
        Category {currentCategoryIndex + 1}/{data.categories.length}, 
        Question {currentQuestionIndex + 1}/{currentCategory.questions.length}
      </div>
      
      <div className="legal-footnote">
        <p>NOTE: Your answers are for conversational purposes only and create no legal obligations 
        or directives. Medical decisions should be made with qualified healthcare providers.</p>
      </div>
    </div>
  );
}

export default QuestionFlow;